import { useState } from "react";
import type { PlayerInfo } from "@/types/api";
import { fetchAuthed, ApiError } from "@/utils/api";
import Badge from "@/components/ui/Badge";
import Modal from "@/components/ui/Modal";
import { Crown, ShieldCheck, ShieldOff, User, Users, UserX } from "lucide-react";

interface PlayerListProps {
  gameId: string;
  players: PlayerInfo[];
  isOwner: boolean;
  onUpdate: () => void;
}

export default function PlayerList({
  gameId,
  players,
  isOwner,
  onUpdate,
}: PlayerListProps) {
  const [toggling, setToggling] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [kickTarget, setKickTarget] = useState<PlayerInfo | null>(null);
  const [kickLoading, setKickLoading] = useState(false);
  const [kickError, setKickError] = useState<string | null>(null);

  const handleToggleMod = async (playerId: string, current: boolean) => {
    setToggling(playerId);
    setToggleError(null);
    try {
      await fetchAuthed(gameId, `/games/${gameId}/players/${playerId}`, {
        method: "PATCH",
        body: { isModerator: !current },
      });
      onUpdate();
    } catch (err) {
      setToggleError(
        err instanceof ApiError ? err.message : "Failed to update moderator status",
      );
    } finally {
      setToggling(null);
    }
  };

  const handleKick = async () => {
    if (!kickTarget) return;
    setKickLoading(true);
    setKickError(null);
    try {
      await fetchAuthed(gameId, `/games/${gameId}/players/${kickTarget.playerId}`, {
        method: "DELETE",
      });
      setKickTarget(null);
      onUpdate();
    } catch (err) {
      setKickError(
        err instanceof ApiError ? err.message : "Failed to remove player",
      );
    } finally {
      setKickLoading(false);
    }
  };

  return (
    <>
      <ul className="space-y-2">
        {toggleError && (
          <li className="rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
            {toggleError}
          </li>
        )}
        {players.map((p) => (
          <li
            key={p.playerId}
            className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-sunken">
              {p.role === "owner" ? (
                <Crown className="h-4 w-4 text-primary" />
              ) : (
                <User className="h-4 w-4 text-text-secondary" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium text-text">{p.displayName}</p>
              <div className="mt-1 flex gap-1.5">
                <Badge variant={p.role === "owner" ? "primary" : "secondary"}>
                  {p.role}
                </Badge>
                {p.isModerator && (
                  <Badge variant="accent">
                    <ShieldCheck className="mr-0.5 inline h-3 w-3" />
                    mod
                  </Badge>
                )}
              </div>
            </div>
            {isOwner && p.role !== "owner" && (
              <div className="flex items-center gap-1.5">
                <button
                  disabled={toggling === p.playerId}
                  onClick={() => handleToggleMod(p.playerId, p.isModerator)}
                  className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    p.isModerator
                      ? "bg-error/10 text-error hover:bg-error/20"
                      : "bg-accent/10 text-accent hover:bg-accent/20"
                  } disabled:opacity-50`}
                >
                  {toggling === p.playerId
                    ? "..."
                    : p.isModerator
                      ? <><ShieldOff className="h-3.5 w-3.5" /> Remove Mod</>
                      : <><ShieldCheck className="h-3.5 w-3.5" /> Make Mod</>}
                </button>
                <button
                  onClick={() => setKickTarget(p)}
                  className="inline-flex items-center gap-1 rounded-lg bg-error/10 px-2 py-1.5 text-sm font-medium text-error transition-colors hover:bg-error/20"
                  title="Remove player"
                >
                  <UserX className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </li>
        ))}
        {players.length === 0 && (
          <div className="flex flex-col items-center py-8 text-text-secondary">
            <Users className="mb-2 h-8 w-8 opacity-50" />
            <p className="text-base">No players yet</p>
          </div>
        )}
      </ul>

      <Modal
        open={kickTarget !== null}
        title="Remove Player?"
        message={`Remove ${kickTarget?.displayName ?? "this player"} from the game? They will need to rejoin with the invite code.`}
        confirmLabel="Remove"
        confirmVariant="danger"
        onConfirm={handleKick}
        onCancel={() => {
          setKickTarget(null);
          setKickError(null);
        }}
        loading={kickLoading}
        error={kickError}
      />
    </>
  );
}
